import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getTasks, Task, updateTaskStatus } from '../lib/tasks';
import { useBusiness } from './BusinessContext';
import { useSupabase } from './SupabaseContext';

interface TasksContextType {
  tasks: Task[];
  isLoading: boolean;
  error: Error | null;
  loadTasks: () => Promise<void>;
  updateTask: (taskId: string, newStatus: string) => Promise<void>;
  addTask: (task: Task) => void;
  selectedResource: string | null;
  setSelectedResource: (resourceId: string | null) => void;
}

const TasksContext = createContext<TasksContextType | undefined>(undefined);

const sortTasksByDate = (tasks: Task[]): Task[] => {
  return [...tasks].sort((a, b) => {
    const dateA = new Date(a.created_at).getTime();
    const dateB = new Date(b.created_at).getTime();
    return dateB - dateA;
  });
};

export function TasksProvider({ children }: { children: React.ReactNode }) {
  const { selectedBusiness } = useBusiness();
  const supabaseClient = useSupabase();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [selectedResource, setSelectedResource] = useState<string | null>(null);

  const addTask = useCallback((task: Task) => {
    setTasks(prev => {
      const exists = prev.some(t => t.id === task.id);
      if (exists) {
        return sortTasksByDate(prev.map(t => t.id === task.id ? task : t));
      }
      return sortTasksByDate([...prev, task]);
    });
  }, []);

  const loadTasks = async () => {
    if (!selectedBusiness?.id || !supabaseClient) {
      console.log('Cannot load tasks - missing business or supabase client');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const tasksList = await getTasks(selectedBusiness.id, supabaseClient);
      console.log('Tasks loaded:', tasksList);
      setTasks(sortTasksByDate(tasksList));
    } catch (error) {
      console.error('Error loading tasks:', error);
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateTask = async (taskId: string, newStatus: string) => {
    if (!supabaseClient) {
      const error = new Error('Supabase client not initialized');
      setError(error);
      throw error;
    }
    setError(null);
    try {
      const updatedTasks = await updateTaskStatus(taskId, newStatus, supabaseClient);
      setTasks(prev => {
        const otherTasks = prev.filter(t => !updatedTasks.find(ut => ut.id === t.id));
        return sortTasksByDate([...updatedTasks, ...otherTasks]);
      });
    } catch (error) {
      console.error('Error updating task status:', error);
      setError(error as Error);
      throw error;
    }
  };

  // Filter tasks based on selected resource
  const filteredTasks = selectedResource 
    ? tasks.filter(task => task.resource_id === selectedResource)
    : tasks;

  // Load tasks when business changes
  useEffect(() => {
    loadTasks();
  }, [selectedBusiness?.id]);

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedBusiness?.id || !supabaseClient) return;

    console.log('Setting up tasks subscription for business:', selectedBusiness.id);

    const channel = supabaseClient
      .channel(`tasks-${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tasks',
          filter: `business_id=eq.${selectedBusiness.id}`
        },
        (payload) => {
          console.log('Received task change:', payload);
          if (payload.eventType === 'INSERT') {
            addTask(payload.new as Task);
          } else if (payload.eventType === 'UPDATE') {
            addTask(payload.new as Task);
          } else if (payload.eventType === 'DELETE') {
            setTasks(prev => prev.filter(task => task.id !== payload.old.id));
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to tasks channel');
        } else {
          console.error('Failed to subscribe to tasks channel:', status);
          setError(new Error(`Failed to subscribe to tasks channel: ${status}`));
          // Retry loading tasks if subscription fails
          loadTasks();
        }
      });

    return () => {
      console.log('Cleaning up tasks subscription');
      supabaseClient.removeChannel(channel);
    };
  }, [selectedBusiness?.id, addTask, supabaseClient]);

  const value = {
    tasks: filteredTasks,
    isLoading,
    error,
    loadTasks,
    updateTask,
    addTask,
    selectedResource,
    setSelectedResource
  };

  return (
    <TasksContext.Provider value={value}>
      {children}
    </TasksContext.Provider>
  );
}

export function useTasks() {
  const context = useContext(TasksContext);
  if (!context) {
    throw new Error('useTasks must be used within a TasksProvider');
  }
  return context;
} 